import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

interface ChartLine {
  dataKey: string
  name: string
  color: string
  strokeDasharray?: string
}

interface TrendChartProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[]
  xKey: string
  lines: ChartLine[]
  title?: string
  height?: number
  type?: 'line' | 'area' | 'bar'
}

export function TrendChart({
  data,
  xKey,
  lines,
  title,
  height = 300,
  type = 'line',
}: TrendChartProps) {
  const formatDate = (value: unknown) => {
    if (typeof value !== 'string') return String(value)
    const date = new Date(value)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const commonProps = {
    data,
    margin: { top: 10, right: 30, left: 0, bottom: 0 },
  }

  const renderChart = () => {
    if (type === 'area') {
      return (
        <AreaChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
          <XAxis
            dataKey={xKey}
            tickFormatter={formatDate}
            tick={{ fontSize: 12, fill: '#605e5c' }}
          />
          <YAxis tick={{ fontSize: 12, fill: '#605e5c' }} />
          <Tooltip
            labelFormatter={formatDate}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e8e8e8',
              borderRadius: 4,
            }}
          />
          <Legend />
          {lines.map((line) => (
            <Area
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color}
              fill={line.color}
              fillOpacity={0.3}
              stackId="1"
            />
          ))}
        </AreaChart>
      )
    }

    if (type === 'bar') {
      return (
        <BarChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
          <XAxis
            dataKey={xKey}
            tickFormatter={formatDate}
            tick={{ fontSize: 12, fill: '#605e5c' }}
          />
          <YAxis tick={{ fontSize: 12, fill: '#605e5c' }} />
          <Tooltip
            labelFormatter={formatDate}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e8e8e8',
              borderRadius: 4,
            }}
          />
          <Legend />
          {lines.map((line) => (
            <Bar
              key={line.dataKey}
              dataKey={line.dataKey}
              name={line.name}
              fill={line.color}
              stackId="stack"
            />
          ))}
        </BarChart>
      )
    }

    // Default: line chart
    return (
      <LineChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
        <XAxis
          dataKey={xKey}
          tickFormatter={formatDate}
          tick={{ fontSize: 12, fill: '#605e5c' }}
        />
        <YAxis tick={{ fontSize: 12, fill: '#605e5c' }} />
        <Tooltip
          labelFormatter={formatDate}
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e8e8e8',
            borderRadius: 4,
          }}
        />
        <Legend />
        {lines.map((line) => (
          <Line
            key={line.dataKey}
            type="monotone"
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.color}
            strokeWidth={2}
            strokeDasharray={line.strokeDasharray}
            dot={false}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    )
  }

  return (
    <div>
      {title && (
        <h3 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 600, color: '#323130' }}>
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  )
}
